import { useEffect } from 'react';
import { isAxiosError } from 'axios';
import { adapter } from '../providers/adapter';
import { authService } from '../api/auth.service';
import { useAuthStore } from '@/core/stores/auth.store';
import type { AuthTokens } from '../types';

/**
 * Classifies a bootstrap getProfile failure.
 * 401/403 = the provider authenticated the user but the backend rejects them
 * (wrong environment, account not provisioned). Retrying won't help → terminal.
 * Anything else (network, 5xx, timeout) is transient and worth retrying.
 */
function isTerminalAuthError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === 401 || status === 403;
}

export function useAuthInit(): void {
  const setLoading = useAuthStore((s) => s.setLoading);
  const setSession = useAuthStore((s) => s.setSession);
  const setTokens = useAuthStore((s) => s.setTokens);
  const clearSession = useAuthStore((s) => s.clearSession);
  const failAuthInit = useAuthStore((s) => s.failAuthInit);

  useEffect(() => {
    let cancelled = false;
    // Flag para evitar llamadas concurrentes a getProfile dentro de la misma invocación
    let profileFetchInFlight = false;

    async function fetchAndSetSession(tokens: AuthTokens): Promise<void> {
      if (profileFetchInFlight) return;
      profileFetchInFlight = true;
      try {
        // Store the token before getProfile so the httpClient injects it on that
        // bootstrap request — otherwise it goes out unauthenticated and 401s.
        setTokens(tokens);
        const user = await authService.getProfile();
        if (!cancelled) setSession(user, tokens);
      } catch (error) {
        console.error('[useAuthInit] getProfile failed:', error);
        if (cancelled) return;
        if (isTerminalAuthError(error)) {
          // Backend rejected the session: clear our state AND the provider session
          // so a reload doesn't loop on the same failing token.
          clearSession();
          await adapter.logout().catch(() => {});
          failAuthInit('auth');
        } else {
          // Transient: keep the provider session so a retry can succeed.
          failAuthInit('network');
        }
      } finally {
        profileFetchInFlight = false;
      }
    }

    // Verifica si hay sesión activa al montar (recarga de página)
    adapter.getSession().then(async (tokens) => {
      if (cancelled) return;
      if (tokens) {
        await fetchAndSetSession(tokens);
      }
      if (!cancelled) setLoading(false);
    });

    // Escucha cambios de sesión del proveedor
    // Cubre: INITIAL_SESSION, SIGNED_IN (OAuth), TOKEN_REFRESHED, SIGNED_OUT
    const unsubscribe = adapter.onSessionChange(async (tokens: AuthTokens | null) => {
      if (cancelled) return;
      if (!tokens) {
        clearSession();
        return;
      }

      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        // TOKEN_REFRESHED: ya hay usuario, solo actualiza los tokens
        setTokens(tokens);
      } else {
        // INITIAL_SESSION / SIGNED_IN vía OAuth: no hay usuario aún
        await fetchAndSetSession(tokens);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [setLoading, setSession, setTokens, clearSession, failAuthInit]);
}
